import React, { useState, useEffect } from 'react';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import GradientDivider from '../../components/gradientDivider';
import PrivacyPolicy from '../../components/PrivacyPolicy/index';
import TermsOfService from '../../components/TermsOfService/index';
import useStyles from './style';
import Element from '../../components/element';
import { colors } from '../../config/theme';
import Flex from '../../components/flex';
import PrivacyModal from '../../components/Privacy/privacyModal';
import * as yup from 'yup';
import {
  LOOKUP_COUNTRIES,
  REGISTER,
  RESET_PASSWORD,
  USER_URL
} from '../../api/endpoints';
import { apiRequest } from '../../api/actions';
import IconWarning from '../../assets/icons/IconWarning.svg';
import Text from '../../components/text';
import { signUpSchema } from '../../config/schema';
import TextField from '../../components/TextField';
import Select from '../../components/Select';
import Toggle from '../../components/Toggle';
import { get, trim, isEmpty } from 'lodash';
import useToast from '../../components/Toast/useToast';
import DatePicker from 'react-datepicker';
import { setUser } from '../../root/actions';
import { ReactComponent as LogoSVG } from '../../assets/icons/ThorneHealthLogo.svg';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-phone-input-2/lib/style.css';

const schema = yup.object(signUpSchema).required();
const getUser = state => {
  return state.containers.app.get('user');
};

const Signup = ({ editable, closeModal }) => {
  const initializeUser = () => {
    return {
      fname: user.first_name,
      lname: user.last_name,
      email: user.email,
      emailAgain: user.email,
      phone: user.phone,
      dob: user?.dob ? new Date(user.dob) : '',
      gender: user.gender,
      race: user.race,
      ethnicity: user.ethnicity,
      education: user.highestEducation,
      address: user.streetAddr1,
      address2: user.streetAddr2,
      city: user.city,
      residence: user.countryId,
      state: user.stateId,
      zip: user.postalCode,
      language: user.language,
      hand: user.hand,
      aboutUs: editable
        ? user.hearAboutUs
          ? user.hearAboutUs
          : 'NA'
        : user.hearAboutUs,
      notificationEmail: user.notificationPreferences.email,
      notificationSms: user.notificationPreferences.sms
    };
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getUser);
  const classes = useStyles();
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [openSnackbar] = useToast();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    resetField
  } = useForm({
    defaultValues: editable ? initializeUser() : { dob: '' },
    resolver: yupResolver(schema)
  });
  const countryWatcher = watch('residence');
  const { isDirty } = useFormState({
    control
  });

  const formUserRegistrationPayload = formData => {
    return {
      first_name: trim(formData.fname),
      last_name: trim(formData.lname),
      email: formData.email,
      phone: formData.phone,
      dob: moment(formData.dob).format('MM/DD/YYYY'),
      gender: formData.gender,
      race: formData.race,
      ethnicity: formData.ethnicity,
      highestEducation: formData.education,
      streetAddr1: trim(formData?.address),
      streetAddr2: trim(formData?.address2),
      city: trim(formData.city),
      countryId: parseInt(formData.residence),
      stateId: parseInt(formData.state),
      postalCode: trim(formData.zip),
      language: formData.language,
      hand: formData.hand,
      hearAboutUs: formData.aboutUs,
      notificationPreferences: {
        email: formData.notificationEmail,
        sms: formData.notificationSms
      }
    };
  };

  const onSubmit = data => {
    const payload = formUserRegistrationPayload(data);
    const request = {
      method: editable ? 'put' : 'post',
      url: editable ? USER_URL : REGISTER,
      data: payload
    };
    const meta = {
      onSuccess: res => {
        if (editable && !isEmpty(res?.data?.data)) {
          dispatch(setUser(res.data.data));
          closeModal();
          openSnackbar('Profile changes saved');
          return;
        }
        if (!res.data.error) {
          navigate('/questionarie', {
            replace: true,
            state: { mrn: get(res, 'data.data.mrn') }
          });
        } else {
          openSnackbar(
            get(res, 'data.error.message') ||
              'Something went wrong. Please try again.',
            'warn'
          );
        }
      }
    };

    dispatch(apiRequest(request, meta));
  };

  const togglePrivacyModal = () => {
    setShowPrivacyModal(!showPrivacyModal);
  };

  const goToLanding = () => {
    navigate('/');
  };

  const displayError = key => (
    <Flex marginTop="5px">
      <Element type="img" attrs={{ src: IconWarning }} marginRight="5px" />
      <Text family="GTAM" color={colors.ceriseRed}>
        {errors[key]?.message}
      </Text>
    </Flex>
  );

  const validateNumber = e => {
    const value = e.target.value;
    // Allow only numbers, including 0
    if (!/^\d*$/.test(value)) {
      e.target.value = value.slice(0, -1);
    }
  };

  const onClickResetPassword = () => {
    const { email } = user;
    const request = {
      method: 'post',
      url: RESET_PASSWORD,
      data: { email }
    };
    const meta = {
      onSuccess: data => {
        if (data.data.error?.code) {
          openSnackbar(data.data.message, 'warn');
          return;
        }
        openSnackbar('You will get an email to reset the password');
      }
    };

    dispatch(apiRequest(request, meta));
  };

  useEffect(() => {
    // if (editable) {
    //   setInitialFormData(initializeUser());
    // }
    const request = {
      method: 'get',
      url: LOOKUP_COUNTRIES
    };
    const meta = {
      onSuccess: data => {
        const allCountries = data?.data?.data;
        setCountries(allCountries);
      }
    };

    dispatch(apiRequest(request, meta));
  }, []);

  useEffect(() => {
    if (countryWatcher) {
      const request = {
        method: 'get',
        url: `/lookup/countries/${parseInt(countryWatcher)}/states`
      };
      const meta = {
        onSuccess: data => {
          const allStates = data?.data?.data;
          setStates(allStates);
        }
      };

      dispatch(apiRequest(request, meta));
    }
  }, [countryWatcher]);

  const attrs = {
    width: '80%',
    margin: 'auto',
    backgroundColor: 'white',
    padding: '40px'
  };
  return (
    <Element {...(!editable && attrs)}>
      {!editable && (
        <>
          <Element type={LogoSVG} width="25%" />
          <GradientDivider />
          <Element
            type="h4"
            fontSize="21px"
            fontFamily="GTAM"
            color={colors.mineShaft}
            margin="8px 0"
          >
            Register for an account
          </Element>
          <Element
            type="p"
            fontFamily="GTAR"
            fontSize="14px"
            marginBottom="35px"
          >
            Get started by filling in your personal information. All fields are
            required unless noted otherwise.
          </Element>
        </>
      )}
      {editable && (
        <Element type="p" fontSize="14px" fontFamily="GTAM" margin="8px 0 35px">
          Make changes to your profile below. Click on “Save Changes” when
          you’re done.
        </Element>
      )}
      <form
        onSubmit={handleSubmit(onSubmit, errors =>
          console.log(JSON.stringify(errors, null, 2))
        )}
      >
        <Flex>
          <div className={classes.field}>
            <TextField
              name="fname"
              label="First Name"
              errors={errors}
              register={register}
            />
          </div>
          <div className={classes.field}>
            <TextField
              name="lname"
              label="Last Name"
              errors={errors}
              register={register}
            />
          </div>
        </Flex>
        <Flex>
          <div className={classes.field}>
            <TextField
              name="email"
              label="Email"
              errors={errors}
              register={register}
              disabled={editable}
              style={{ ...(editable && { borderBottom: 'none' }) }}
            />
          </div>
          {!editable && (
            <div className={classes.field}>
              <TextField
                name="emailAgain"
                label="Email Again"
                errors={errors}
                register={register}
              />
            </div>
          )}
          {editable && (
            <div className={classes.field}>
              <label htmlFor="phone">Phone Number</label>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <NumberFormat
                    {...field}
                    placeholder="+1 (123) 456 - 7890"
                    type="tel"
                    format="+1 (###) ###-####"
                    value={field.value.slice(2)}
                    className={classes.phoneField}
                  />
                )}
              />
              {errors.phone?.message && displayError('phone')}
            </div>
          )}
        </Flex>
        {!editable && (
          <div className={classes.field}>
            <label htmlFor="phone">Phone Number</label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <NumberFormat
                  {...field}
                  placeholder="+1 (123) 456 - 7890"
                  type="tel"
                  format="+1 (###) ###-####"
                  className={classes.phoneField}
                />
              )}
            />
            {errors.phone?.message && displayError('phone')}
          </div>
        )}
        <Flex>
          <div className={classes.field}>
            <label htmlFor="dob">Date of Birth (MM/DD/YYYY)</label>
            <Controller
              name="dob"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  selected={getValues('dob')}
                  // onChange={date => {
                  //   let e = {
                  //     target: {
                  //       name: 'date',
                  //       value: date
                  //     }
                  //   };
                  //   onChange(e);
                  // }}
                  // excludeDates={blockedDates.map(date => new Date(date))}
                  minDate={moment()
                    .subtract(125, 'years')
                    .toDate()}
                  maxDate={moment().toDate()}
                  placeholderText="MM/DD/YYYY"
                  showYearDropdown
                  // dateFormatCalendar="MMMM"
                  yearDropdownItemNumber={125}
                  scrollableYearDropdown
                  calendarClassName={classes.calendar}
                />
              )}
            />
            {errors.dob?.message && displayError('dob')}
          </div>
          <div className={classes.field}>
            <label>Gender</label>
            <Flex>
              <div className={classes.radioField}>
                <input
                  type="radio"
                  id="male"
                  {...register('gender')}
                  value="Male"
                  className={classes.radioInput}
                />
                <label htmlFor="male">Male</label>
              </div>
              <div className={classes.radioField}>
                <input
                  type="radio"
                  id="female"
                  {...register('gender')}
                  value="Female"
                  className={classes.radioInput}
                />
                <label htmlFor="female">Female</label>
              </div>
            </Flex>
            {errors.gender?.message && displayError('gender')}
          </div>
        </Flex>
        <Flex>
          <div className={classes.field}>
            <label>Race (Please select your primary race)</label>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="american"
                {...register('race')}
                value="American Indian or Alaska Native"
                className={classes.radioInput}
              />
              <label htmlFor="american">American Indian or Alaska Native</label>
            </div>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="asian"
                {...register('race')}
                value="Asian"
                className={classes.radioInput}
              />
              <label htmlFor="asian">Asian</label>
            </div>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="african"
                {...register('race')}
                value="Black or African American"
                className={classes.radioInput}
              />
              <label htmlFor="african">Black or African American</label>
            </div>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="hawaiian"
                {...register('race')}
                value="Native Hawaiian or Other Pacific Islanders"
                className={classes.radioInput}
              />
              <label htmlFor="hawaiian">
                Native Hawaiian or Other Pacific Islanders
              </label>
            </div>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="white"
                {...register('race')}
                value="White"
                className={classes.radioInput}
              />
              <label htmlFor="white">White</label>
            </div>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="other"
                {...register('race')}
                value="other"
                className={classes.radioInput}
              />
              <label htmlFor="other">Other</label>
            </div>
            {errors.race?.message && displayError('race')}
          </div>
          <div className={classes.field}>
            <label>Ethnicity (Please select your primary ethnicity)</label>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="hispanic"
                {...register('ethnicity')}
                value="Hispanic or Latino"
                className={classes.radioInput}
              />
              <label htmlFor="hispanic">Hispanic or Latino</label>
            </div>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="non-hispanic"
                {...register('ethnicity')}
                value="Non-Hispanic or Latino"
                className={classes.radioInput}
              />
              <label htmlFor="non-hispanic">Non-Hispanic or Latino</label>
            </div>
            {errors.ethnicity?.message && displayError('ethnicity')}
          </div>
        </Flex>
        <div className={classes.field}>
          <Select
            label="What is the highest level of your education?"
            name="education"
            register={register}
            mainOption="Select Education"
            options={[                    
              {
                name: 'Associates degree (e.g., AA, AS)',
                value: 'Associates degree (e.g., AA, AS)'
              },
              {
                name: 'Bachelor’s degree (e.g., BA, AB, BS)',
                value: 'Bachelor’s degree (e.g., BA, AB, BS)'
              },
              {
                name: 'Doctorate degree (e.g., PhD, EdD)',
                value: 'Doctorate degree (e.g., PhD, EdD)'
              },
              { name: 'GED', value: 'GED' },
              {
                name: 'Master’s degree (e.g., MA, MS, MEng, MEd, MSW, MBA)',
                value: 'Master’s degree (e.g., MA, MS, MEng, MEd, MSW, MBA)'
              },
              {
                name: 'Professional degree (e.g., MD, DDS, DVM, LLB, JD)',
                value: 'Professional degree (e.g., MD, DDS, DVM, LLB, JD)'
              },
              {
                name: 'Some college credit but less than 1 year',
                value: 'Some college credit but less than 1 year'
              }              
            ]}
            errors={errors}
          />
        </div>
        <div className={classes.field}>
          <label>Are you Left or Right Handed?</label>
          <Flex>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="left"
                {...register('hand')}
                value="Left"
                className={classes.radioInput}
              />
              <label htmlFor="left">Left</label>
            </div>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="right"
                {...register('hand')}
                value="Right"
                className={classes.radioInput}
              />
              <label htmlFor="right">Right</label>
            </div>
          </Flex>
          {errors.hand?.message && displayError('hand')}
        </div>
        <div className={classes.field}>
          <Select
            label="Country of Residence"
            name="residence"
            register={register}
            mainOption="Select Country"
            options={countries.map(country => {
              return { name: country.name, value: country._id };
            })}
            selectedValue={getValues('residence')}
            onValueChange={() => resetField('state', { defaultValue: '' })}
            errors={errors}
          />
        </div>
        <Flex>
          <div className={classes.field}>
            <TextField
              name="address"
              label="Street Address"
              errors={errors}
              register={register}
            />
          </div>
          <div className={classes.field}>
            <TextField
              name="address2"
              label="&nbsp;"
              errors={errors}
              register={register}
              placeholder=" P.O Box, Building, Space, etc."
            />
          </div>
        </Flex>
        <Flex>
          <div className={classes.field}>
            <TextField
              name="city"
              label="City"
              errors={errors}
              register={register}
            />
          </div>
          <div className={classes.field}>
            <Flex>
              <div className={classes.field}>
                <Select
                  label="State/Province"
                  name="state"
                  register={register}
                  mainOption="Select State"
                  options={states.map(state => {
                    return { name: state.name, value: state._id };
                  })}
                  errors={errors}
                  selectedValue={getValues('state')}
                />
              </div>
              <Element
                className={`${classes.field} ${classes.margin5}`}
                width="100%"
                marginRight="unset"
              >
                <TextField
                  type="text"
                  onChange={validateNumber}
                  name="zip"
                  label="Zip or Postal Code"
                  errors={errors}
                  register={register}
                />
              </Element>
            </Flex>
          </div>
        </Flex>
        <div className={classes.field}>
          <label>Preferred Language</label>
          <Flex>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="english"
                {...register('language')}
                value="English"
                className={classes.radioInput}
              />
              <label htmlFor="english">English</label>
            </div>
            <div className={classes.radioField}>
              <input
                type="radio"
                id="spanish"
                {...register('language')}
                value="Spanish"
                className={classes.radioInput}
              />
              <label htmlFor="spanish">Spanish</label>
            </div>
          </Flex>
          {errors.language?.message && displayError('language')}
        </div>
        {editable && (
          <>
            <Element className={classes.notificationText}>
              <Element type="label" fontSize="16px">
                Notification Preferences
              </Element>
              <Flex>
                <Flex align="center" marginRight="30px">
                  <Controller
                    name="notificationEmail"
                    control={control}
                    render={({ field }) => (
                      <Toggle
                        {...field}
                        disabled={true}
                        value={getValues('notificationEmail')}
                      />
                    )}
                  />
                  <Element
                    type="p"
                    fontFamily="GTAR"
                    fontSize="14px"
                    marginLeft="4px"
                  >
                    Email Notifications
                  </Element>
                </Flex>
                <Flex align="center">
                  <Controller
                    name="notificationSms"
                    control={control}
                    render={({ field }) => (
                      <Toggle {...field} value={getValues('notificationSms')} />
                    )}
                  />
                  <Element
                    type="p"
                    fontFamily="GTAR"
                    fontSize="14px"
                    marginLeft="4px"
                  >
                    SMS Notifications
                  </Element>
                </Flex>
              </Flex>
            </Element>
            <Element type="div" className={classes.resetPasswordText}>
              <Element type="label" fontSize="16px">
                Password Reset
              </Element>
              <Element type="p" fontFamily="GTAR" fontSize="14px">
                Click the button below to receive password reset information
                sent to your email.
              </Element>
            </Element>
            <div className={classes.resetPassword}>
              <Element
                // type="button"
                className={classes.btn}
                fontFamily="GTAM"
                color={colors.mineShaft}
                onClick={onClickResetPassword}
              >
                Reset Password
              </Element>
            </div>
          </>
        )}
        {!editable && (
          <>
            <div className={classes.field}>
              <label>How did you hear about us?</label>
              <div className={classes.radioField}>
                <input
                  type="radio"
                  id="doctor"
                  {...register('aboutUs')}
                  value="Doctor or other Health Professional"
                  className={classes.radioInput}
                />
                <label htmlFor="doctor">
                  Doctor or other Health Professional
                </label>
              </div>
              <div className={classes.radioField}>
                <input
                  type="radio"
                  id="friend"
                  {...register('aboutUs')}
                  value="Friend"
                  className={classes.radioInput}
                />
                <label htmlFor="friend">Friend</label>
              </div>
              <div className={classes.radioField}>
                <input
                  type="radio"
                  id="se"
                  {...register('aboutUs')}
                  value="Search Engine"
                  className={classes.radioInput}
                />
                <label htmlFor="se">Search Engine</label>
              </div>
              <div className={classes.radioField}>
                <input
                  type="radio"
                  id="sm"
                  {...register('aboutUs')}
                  value="Social Media"
                  className={classes.radioInput}
                />
                <label htmlFor="sm">Social Media</label>
              </div>
              <div className={classes.radioField}>
                <input
                  type="radio"
                  id="adv"
                  {...register('aboutUs')}
                  value="Other Advertisement"
                  className={classes.radioInput}
                />
                <label htmlFor="adv">Other Advertisement</label>
              </div>
              {errors.aboutUs?.message && displayError('aboutUs')}
            </div>
            <Element width="55%" marginRight="20px" marginBottom="30px">
              <label>Privacy Policy</label>
              <div className={classes.privacyBlock}>
                <PrivacyPolicy />
                <div className={classes.radioField}>
                  <input
                    type="checkbox"
                    id="privacy"
                    {...register('privacy')}
                    value={true}
                    className={classes.radioInput}
                  />
                  <label htmlFor="privacy">
                    By checking here you agree to the above privacy policy.
                  </label>
                </div>
              </div>
              {errors.privacy?.message && displayError('privacy')}
            </Element>
            <Element width="55%" marginRight="20px" marginBottom="30px">
              <label>Terms of Service</label>
              <div className={classes.privacyBlock}>
                <TermsOfService />
                <div className={classes.radioField}>
                  <input
                    type="checkbox"
                    id="terms"
                    {...register('terms')}
                    value={true}
                    className={classes.radioInput}
                  />
                  <label htmlFor="terms">
                    By checking here you agree to the above terms of service.
                  </label>
                </div>
              </div>
              {errors.terms?.message && displayError('terms')}
            </Element>
          </>
        )}
        <Flex>
          <button
            className={classes.btn}
            onClick={editable ? closeModal : goToLanding}
          >
            Cancel
          </button>
          <button
            className={`${classes.btn} ${classes.submit}`}
            type="submit"
            disabled={!isDirty}
          >
            {editable ? 'Save Changes' : 'Submit'}
          </button>
        </Flex>
      </form>
      {!editable && (
        <p className={classes.footer}>
          © {new Date().getFullYear()} Thorne Healthcare ・{' '}
          <span onClick={togglePrivacyModal}>Privacy Policy</span>
        </p>
      )}
      {showPrivacyModal && (
        <PrivacyModal isPrivacy onClose={togglePrivacyModal} />
      )}
    </Element>
  );
};

export default Signup;
